import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AuthContext } from 'libs.react_internal';
import { buildQueryFromState } from './query_builder.utils';
import { withSortTiebreakers } from '../../../data_reports/utils';
import { getCubeApi } from '../../../transfer_templates/hooks/use_cube_api/use_cube_api.hook';
export { buildPivotConfigFromState, buildQueryFromState } from './query_builder.utils';
// =============================================================================
// Hook
// =============================================================================
/**
 * Hook that manages Cube.js query execution for the chart builder preview.
 * The query is only executed when `executeQuery()` is called (i.e. on "Run").
 */
export const useChartQuery = ({ state, fallbackQuery, chartResource, additionalCubeConfigIds = [], }) => {
    const { entityId } = useContext(AuthContext);
    const [resultSet, setResultSet] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [hasRun, setHasRun] = useState(false);
    const abortControllerRef = useRef(null);
    const cubeConfigIds = useMemo(() => {
        const ids = new Set();
        const chartData = chartResource?.data;
        const chartCubeConfigId = chartResource?.cubeConfigId || chartData?.cubeConfigId || undefined;
        if (chartCubeConfigId) {
            ids.add(chartCubeConfigId);
        }
        additionalCubeConfigIds.forEach((id) => {
            if (id) {
                ids.add(id);
            }
        });
        return [...ids];
    }, [chartResource, additionalCubeConfigIds]);
    // Create a stable Cube.js API client based on study/env/entity
    const cubeApi = useMemo(() => {
        if (!state.studyId || !state.testEnvironmentId || !entityId) {
            return null;
        }
        return getCubeApi({
            entityId: entityId.toString(),
            studyId: state.studyId,
            activeReportsTab: 0, // Chart builder always uses test environment
            selectedTestEnv: { label: '', value: state.testEnvironmentId },
            prodOrgs: [],
            env: 'test',
            cubeConfigIds,
        });
    }, [entityId, state.studyId, state.testEnvironmentId, cubeConfigIds]);
    const executeQuery = useCallback(async (options) => {
        // Always show loading state immediately so the UI reacts to the Run click
        setIsLoading(true);
        setError(null);
        setHasRun(true);
        // Try to build a query from the current settings; fall back to the chart's stored query
        const builtQuery = buildQueryFromState(state);
        let query = builtQuery || fallbackQuery || null;
        if (!query) {
            setError(new Error('Cannot build query. Please select dimensions or measures.'));
            setIsLoading(false);
            return;
        }
        // When using the fallback query, dashboard/chart filters from state aren't included
        // by buildQueryFromState, so merge them in manually.
        if (!builtQuery && state.filters?.length) {
            const filterEntries = state.filters
                .filter((f) => f.member && f.operator && f.values.length > 0)
                .map((f) => ({
                member: f.member,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                operator: f.operator,
                values: f.values.map(String),
            }));
            if (filterEntries.length > 0) {
                query = { ...query, filters: filterEntries };
            }
        }
        if (!cubeApi) {
            setError(new Error('Analytics service is not available. Please check your study and environment selection.'));
            setIsLoading(false);
            return;
        }
        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        try {
            const isTable = state.chartType === 'table';
            const defaultPageSize = state.defaultRowsPerPage ?? 10;
            const pageIndex = options?.pageIndex ?? 0;
            const pageSize = options?.pageSize ?? defaultPageSize;
            // For tables, apply user-selected order if provided. Append the chart's
            // other columns as tiebreakers so cube LIMIT/OFFSET stays deterministic
            // when the sorted column has ties (SSP-3889).
            if (isTable && options?.order && Object.keys(options.order).length > 0) {
                const tiebreakerKeys = [...(query.dimensions || []), ...(query.measures || [])];
                query = { ...query, order: withSortTiebreakers(options.order, tiebreakerKeys) };
            }
            // For tables, use server-side pagination
            const paginatedQuery = isTable
                ? {
                    ...query,
                    total: true,
                    offset: pageIndex * pageSize,
                    limit: pageSize,
                }
                : query;
            const result = await cubeApi.load(paginatedQuery, {
                cache: 'must-revalidate',
                signal: controller.signal,
            });
            // Extract total count from the internal load response (tables only)
            if (isTable) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const loadResponse = result.loadResponse?.results?.[0];
                setTotal(loadResponse?.total ?? 0);
            }
            setResultSet(result);
        }
        catch (err) {
            // Silently ignore aborted/canceled requests (different clients use different error shapes)
            const isAbort = err instanceof Error &&
                (err.name === 'AbortError' ||
                    err.name === 'CanceledError' ||
                    (typeof err.message === 'string' && err.message.toLowerCase().includes('abort')));
            if (isAbort) {
                return;
            }
            setError(err instanceof Error ? err : new Error('Failed to execute chart query'));
        }
        finally {
            // Only clear loading if this request wasn't aborted (a newer request may be in flight)
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [state, cubeApi, fallbackQuery]);
    const resetResults = useCallback(() => {
        setResultSet(null);
        setError(null);
        setTotal(0);
    }, []);
    return {
        resultSet,
        isLoading,
        error,
        total,
        hasRun,
        executeQuery,
        resetResults,
    };
};
